import BaseComponent from 'bootstrap/js/src/base-component.js';
import EventHandler from 'bootstrap/js/src/dom/event-handler.js';
import SelectorEngine from 'bootstrap/js/src/dom/selector-engine.js';
import { defineJQueryPlugin } from 'bootstrap/js/src/util/index.js';

const NAME = 'videoModal';
const EVENT_SHOW = 'show.bs.modal';
const EVENT_HIDDEN = 'hidden.bs.modal';
const SELECTOR_MODAL = '.modal';
const SELECTOR_DIALOG = '.modal-dialog-media';
const SELECTOR_RATIO = '.ratio';

// eslint-disable-next-line no-unused-vars
let youTubeAPIReady = false;
const youTubeAPIInitPromise = new Promise((resolve) => {
  if (globalThis.YT?.Player) {
    youTubeAPIReady = true;
    resolve();
  } else {
    const prev = globalThis.onYouTubeIframeAPIReady;
    globalThis.onYouTubeIframeAPIReady = () => {
      try {
        if (typeof prev === 'function') prev();
      } catch {}

      youTubeAPIReady = true;
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.append(script);
  }
});

// eslint-disable-next-line no-unused-vars
let vimeoAPIReady = false;
const vimeoAPIInitPromise = new Promise((resolve) => {
  if (globalThis.Vimeo?.Player) {
    vimeoAPIReady = true;
    resolve();
  } else {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.addEventListener('load', () => {
      vimeoAPIReady = true;
      resolve();
    });

    document.head.append(script);
  }
});

class VideoModal extends BaseComponent {
  constructor(element) {
    super(element);

    this._dialog = SelectorEngine.findOne(SELECTOR_DIALOG, this._element);
    this._container = SelectorEngine.findOne(SELECTOR_RATIO, this._element);
    this._youtubeSrc = this._container?.getAttribute('data-src');
    this._vimeoUrl = this._container?.getAttribute('data-vimeo-url');
    this._youtubePlayer = null;
    this._vimeoPlayer = null;
  }

  dispose() {
    this._onHide();
    super.dispose();
  }

  async _onShow() {
    const { id } = this._element;
    const playerID = `video-${id}-youtube-player`;

    if (this._vimeoUrl) {
      await vimeoAPIInitPromise;
      const vimeoId = this._getVimeoId(this._vimeoUrl);

      this._vimeoPlayer = new globalThis.Vimeo.Player(this._container, {
        id: vimeoId,
        autoplay: true
      });

      this._element.dataset.vimeoId = String(vimeoId);
      this._element.__unlkVimeoPlayer = this._vimeoPlayer;

      this._element.dispatchEvent(
        new CustomEvent('videomodal.ready', {
          bubbles: true,
          detail: { provider: 'vimeo', player: this._vimeoPlayer, videoId: vimeoId }
        })
      );

      return;
    }

    if (this._youtubeSrc) {
      await youTubeAPIInitPromise;
      const youtubeId = this._getYouTubeId(this._youtubeSrc);
      const playerDiv = this._container.querySelector(`#${playerID}`);

      this._youtubePlayer = new globalThis.YT.Player(playerDiv, {
        videoId: youtubeId,
        playerVars: { autoplay: 1, rel: 0 }
      });

      this._element.dataset.ytId = youtubeId;
      this._element.__unlkYtPlayer = this._youtubePlayer;

      this._element.dispatchEvent(
        new CustomEvent('videomodal.ready', {
          bubbles: true,
          detail: { provider: 'youtube', player: this._youtubePlayer, videoId: youtubeId }
        })
      );
    }
  }

  _onHide() {
    if (this._vimeoPlayer) {
      this._vimeoPlayer.unload().catch(() => {});
      this._vimeoPlayer = null;
    }

    if (this._youtubePlayer) {
      this._youtubePlayer.destroy();
      this._youtubePlayer = null;
    }

    delete this._element.__unlkYtPlayer;
    delete this._element.dataset.ytId;
    delete this._element.__unlkVimeoPlayer;
    delete this._element.dataset.vimeoId;
  }

  _getYouTubeId(url) {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);
    return match ? match[1] : '';
  }

  _getVimeoId(url) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : '';
  }

  static get NAME() {
    return NAME;
  }

  static jQueryInterface(config) {
    return this.each(function () {
      VideoModal.getOrCreateInstance(this, config);
    });
  }
}

EventHandler.on(document, EVENT_SHOW, SELECTOR_MODAL, function () {
  if (!SelectorEngine.findOne(SELECTOR_DIALOG, this)) return;
  VideoModal.getOrCreateInstance(this)._onShow();
});

EventHandler.on(document, EVENT_HIDDEN, SELECTOR_MODAL, function () {
  if (!SelectorEngine.findOne(SELECTOR_DIALOG, this)) return;
  VideoModal.getOrCreateInstance(this)._onHide();
});

defineJQueryPlugin(VideoModal);

export default VideoModal;
